import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getTripCascadingFilters } from 'store/tripSliceV2';

/**
 * useCascadingTripFilters
 *
 * @param {object}   params
 * @param {object}   params.paginateConfig
 * @param {boolean}  [params.enabled=true]
 * @param {string[]} [params.requestedFilters]
 * @param {boolean}  [params.isEnded=false]
 * @param {number}   [params.debounceMs=300]
 * @returns {{ options: object, loading: boolean }}
 */
const DEFAULT_DIMENSIONS = ['cities', 'clients', 'projects', 'vendors'];

const ALL_DIMENSION_KEYS = [
	'projects',
	'clients',
	'vendors',
	'vehicles',
	'routes',
	'drivers',
	'cities',
	'trip_types',
	'statuses',
];

const emptyOptions = () => ALL_DIMENSION_KEYS.reduce((acc, key) => ({ ...acc, [key]: [] }), {});
const normalizeOptions = (data = {}) => {
	const out = emptyOptions();
	ALL_DIMENSION_KEYS.forEach((key) => {
		const list = Array.isArray(data[key]) ? data[key] : [];
		out[key] = list.map((item) =>
			typeof item === 'string'
				? { value: item, label: item }
				: { value: item.value ?? item.id, label: item.label ?? item.status ?? item.name }
		);
	});
	return out;
};

const normalizeBusinessUnitIds = (buIds) => {
	if (!buIds) return [];
	const arr = Array.isArray(buIds) ? buIds : [buIds];
	return arr.map((id) => String(id).trim()).filter(Boolean);
};
export const buildTripFilterSelections = (paginateConfig = {}, { isEnded = false } = {}) => {
	const selections = {};
	if (paginateConfig.city) selections.city = paginateConfig.city;
	if (paginateConfig.client_id) selections.client_id = paginateConfig.client_id;
	if (paginateConfig.project_id) selections.project_id = paginateConfig.project_id;
	if (paginateConfig.vendor_id) selections.vendor_id = paginateConfig.vendor_id;
	if (paginateConfig.trip_type) selections.trip_type = paginateConfig.trip_type;
	if (paginateConfig.trip_status) selections.trip_status_id = paginateConfig.trip_status;
	selections.is_ended = Boolean(isEnded);
	const buIds = normalizeBusinessUnitIds(paginateConfig.business_unit_ids);
	if (buIds.length) selections.business_unit_ids = buIds;
	if (paginateConfig.from_date && paginateConfig.from_date !== false) {
		selections.from_date = paginateConfig.from_date;
	}
	if (paginateConfig.to_date && paginateConfig.to_date !== false) {
		selections.to_date = paginateConfig.to_date;
	}
	return selections;
};

const useCascadingTripFilters = ({
	paginateConfig,
	enabled = true,
	requestedFilters = DEFAULT_DIMENSIONS,
	listSearchType = 'trip',
	isEnded = false,
	debounceMs = 300,
} = {}) => {
	const dispatch = useDispatch();
	const [options, setOptions] = useState(emptyOptions);
	const [loading, setLoading] = useState(false);

	const timerRef = useRef(null);
	const seqRef = useRef(0);
	const mountedRef = useRef(true);
	const lastKeyRef = useRef(null);
	// Refs keep `run` referentially stable even if the caller passes fresh values each render.
	const requestedRef = useRef(requestedFilters);
	requestedRef.current = requestedFilters;
	const typeRef = useRef(listSearchType);
	typeRef.current = listSearchType;

	useEffect(
		() => () => {
			mountedRef.current = false;
			if (timerRef.current) clearTimeout(timerRef.current);
		},
		[]
	);

	const run = useCallback(
		(selections) => {
			const requested = requestedRef.current;
			const listType = typeRef.current;
			const key = JSON.stringify({ t: listType, s: selections, r: requested });
			if (key === lastKeyRef.current) return;
			lastKeyRef.current = key;

			seqRef.current += 1;
			const requestId = seqRef.current;
			setLoading(true);
			dispatch(
				getTripCascadingFilters(selections, {
					requestedFilters: requested,
					listSearchType: listType,
				})
			)
				.then((data) => {
					if (!mountedRef.current || requestId !== seqRef.current) return;
					setOptions(normalizeOptions(data));
				})
				.catch(() => {
					if (mountedRef.current && requestId === seqRef.current) {
						lastKeyRef.current = null;
						setOptions(emptyOptions());
					}
				})
				.finally(() => {
					if (mountedRef.current && requestId === seqRef.current) setLoading(false);
				});
		},
		[dispatch]
	);
	const selections = useMemo(
		() => buildTripFilterSelections(paginateConfig, { isEnded }),
		[paginateConfig, isEnded]
	);
	useEffect(() => {
		if (!enabled) return undefined;
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => run(selections), debounceMs);
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, [enabled, selections, debounceMs, run]);

	return { options, loading };
};

export default useCascadingTripFilters;
